import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { productsData } from "../lib/api";
import { ArrowLeft, ChevronRight, Download, Loader2 } from "lucide-react";
import InquiryForm from "../components/InquiryForm";
import ProductCard from "../components/ProductCard";
import { toast } from "sonner";

import { Helmet } from "react-helmet-async";

const ProductDetailPage = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const found = productsData.find((p) => p.slug === slug);
    if (found) {
      setProduct(found);
    } else {
      setError("Product not found");
    }
    setLoading(false);
  }, [slug]);

  const handleDownload = () => {
    const filename = `Gralon_Global_${product.name.replace(/\s+/g, "_")}_Specifications.pdf`;
    const link = document.createElement("a");
    link.href = `/pdfs/${filename}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Specification sheet download started.");
  };

  if (loading) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center" data-testid="product-loading">
        <Loader2 className="w-8 h-8 animate-spin text-forest" strokeWidth={1.5} />
      </main>
    );
  }

  if (error || !product) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center" data-testid="product-error">
        <div className="text-center">
          <div className="font-serif text-3xl text-ink mb-3">Product not found</div>
          <Link to="/products" className="text-forest underline">Back to catalog</Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="product-detail-page" className="bg-cream">
      <Helmet>
        <title>{`${product.name} — Premium India Export | Gralon Global`}</title>
        <meta name="description" content={`High-quality bulk ${product.name} (${product.botanical_name}) exported from India. ${product.description.substring(0, 150)}...`} />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Product",
              "name": "${product.name}",
              "image": "${product.image}",
              "description": "${product.description}",
              "sku": "${product.slug}",
              "brand": {
                "@type": "Brand",
                "name": "Gralon Global"
              },
              "offers": {
                "@type": "Offer",
                "url": "https://www.gralonglobal.com/products/${product.slug}",
                "priceCurrency": "USD",
                "availability": "https://schema.org/InStock",
                "itemCondition": "https://schema.org/NewCondition"
              },
              "additionalProperty": [
                {
                  "@type": "PropertyValue",
                  "name": "Botanical Name",
                  "value": "${product.botanical_name}"
                },
                {
                  "@type": "PropertyValue",
                  "name": "Origin",
                  "value": "${product.origin}"
                }
              ]
            }
          `}
        </script>
      </Helmet>
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-8">
        <nav className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-muted">
          <Link to="/" className="hover:text-forest">Home</Link>
          <ChevronRight className="w-3 h-3" strokeWidth={1.5} />
          <Link to="/products" className="hover:text-forest">Products</Link>
          <ChevronRight className="w-3 h-3" strokeWidth={1.5} />
          <span className="text-forest-dark" data-testid="breadcrumb-current">{product.name}</span>
        </nav>
      </div>

      {/* Header / split layout */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-20 grid lg:grid-cols-12 gap-12">
        {/* Sticky image */}
        <div className="lg:col-span-6">
          <div className="lg:sticky lg:top-28">
            <div className="relative bg-cream-alt aspect-[4/5] overflow-hidden">
              <img
                src={product.image}
                alt={`${product.name} (${product.botanical_name}) - India Based Export`}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="product-image"
              />
              <div className="absolute top-5 left-5 px-3 py-1 bg-cream/95 backdrop-blur-sm">
                <span className="text-[10px] tracking-[0.25em] uppercase text-forest-dark">
                  {product.category}
                </span>
              </div>
            </div>

            {/* Quick facts under image */}
            <div className="mt-6 grid grid-cols-2 gap-px bg-line">
              <div className="bg-cream p-4">
                <div className="text-[10px] tracking-[0.25em] uppercase text-muted">Origin</div>
                <div className="font-serif text-lg text-ink mt-1">{product.origin}</div>
              </div>
              <div className="bg-cream p-4">
                <div className="text-[10px] tracking-[0.25em] uppercase text-muted">Harvest</div>
                <div className="font-serif text-lg text-ink mt-1">{product.harvest_season}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-6">
          <Link to="/products" data-testid="back-to-products" className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-muted hover:text-forest mb-6">
            <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to catalog
          </Link>

          <div className="label-eyebrow">{product.category}</div>
          <h1 className="font-serif font-light mt-4 text-5xl lg:text-7xl leading-[1.02] text-ink" data-testid="product-name">
            {product.name}
          </h1>
          <p className="italic text-muted mt-3 text-lg" data-testid="product-botanical">
            {product.botanical_name}
          </p>
          <div className="editorial-rule mt-8" />
          <p className="mt-8 text-ink/85 leading-relaxed text-lg" data-testid="product-description">
            {product.description}
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <button
              onClick={handleDownload}
              data-testid="download-spec-btn"
              className="inline-flex items-center gap-3 px-8 py-4 bg-forest text-cream text-sm tracking-[0.2em] uppercase hover:bg-forest-dark transition-colors duration-300"
            >
              <Download className="w-4 h-4" strokeWidth={1.5} />
              Download Specifications
            </button>
            <a
              href="#inquiry"
              data-testid="product-inquire-btn"
              className="inline-flex items-center gap-3 px-8 py-4 border border-forest text-forest text-sm tracking-[0.2em] uppercase hover:bg-forest hover:text-cream transition-colors duration-300"
            >
              Inquire about this Product
            </a>
          </div>

          {/* Specifications table */}
          <div className="mt-16">
            <div className="label-eyebrow">Technical Specifications</div>
            <h2 className="font-serif font-light mt-3 text-3xl lg:text-4xl text-ink">
              Quality, measured.
            </h2>
            <div className="mt-8 border-t border-line" data-testid="specs-table">
              {product.specifications.map((s, i) => (
                <div
                  key={i}
                  className="grid grid-cols-12 gap-4 py-4 border-b border-line"
                >
                  <div className="col-span-5 text-[11px] tracking-[0.2em] uppercase text-muted pt-1">
                    {s.label}
                  </div>
                  <div className="col-span-7 font-serif text-lg text-ink">
                    {s.value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits & Applications */}
          <div className="mt-16 grid sm:grid-cols-2 gap-10">
            <div>
              <div className="label-eyebrow">Key Benefits</div>
              <ul className="mt-5 space-y-3">
                {product.benefits.map((b, i) => (
                  <li key={i} className="flex gap-3 text-ink/85">
                    <span className="mt-2 w-1.5 h-1.5 bg-gold flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="label-eyebrow">Applications</div>
              <ul className="mt-5 space-y-3">
                {product.applications.map((a, i) => (
                  <li key={i} className="flex gap-3 text-ink/85">
                    <span className="mt-2 w-1.5 h-1.5 bg-forest flex-shrink-0" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Compliance & Certifications */}
          <div className="mt-16">
            <div className="label-eyebrow">Compliance</div>
            <h3 className="font-serif text-2xl text-ink mt-3">Held by Gralon Global</h3>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="certs-held">
              {["FSSAI Licensed", "IEC Registered", "APEDA Member"].map((c) => (
                <span
                  key={c}
                  className="px-4 py-2 bg-forest text-cream text-xs tracking-[0.2em] uppercase"
                >
                  {c}
                </span>
              ))}
            </div>

            <h3 className="font-serif text-2xl text-ink mt-8">Available on Request</h3>
            <p className="mt-2 text-sm text-muted max-w-xl">
              Custom certifications can be arranged per shipment through our certified partner facilities.
            </p>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="certs-available">
              {product.certifications.map((c) => (
                <span
                  key={c}
                  className="px-4 py-2 border border-forest/30 text-forest-dark text-xs tracking-[0.2em] uppercase"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <section className="py-24 border-t border-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="label-eyebrow">Discover More</div>
          <h2 className="font-serif font-light mt-4 text-4xl text-ink">
            Other <span className="italic text-forest">premium botanicals</span>.
          </h2>
          <div className="mt-12 grid sm:grid-cols-3 gap-10">
            {productsData
              .filter((p) => p.slug !== product.slug)
              .slice(0, 3)
              .map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
          </div>
        </div>
      </section>

      {/* Inquiry */}
      <section id="inquiry" className="bg-cream-alt py-24 lg:py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="label-eyebrow">Inquiry</div>
          <h2 className="font-serif font-light mt-4 text-4xl lg:text-5xl text-ink">
            Request a quote for{" "}
            <span className="italic text-forest">{product.name}</span>.
          </h2>
          <p className="mt-5 text-muted max-w-2xl">
            Share your specification, target market and quantity. Our export team
            responds within one business day with samples and pricing.
          </p>
          <div className="mt-12 bg-cream p-8 lg:p-12 border border-line">
            <InquiryForm defaultProduct={product.name} />
          </div>
        </div>
      </section>
    </main>
  );
};

export default ProductDetailPage;
